import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiFile, FiTrash2, FiUpload } from 'react-icons/fi';
import { IoMdReturnLeft } from 'react-icons/io';
import MainCard from '../../../../components/MainCard/Index';
import { useGetPPPIQDocuments } from '../../../../services/hooks/PPPIQ/useGetPPPIQDocuments';
import { useUploadPPPIQDocument } from '../../../../services/hooks/PPPIQ/useUploadPPPIQDocument';
import { useDeletePPPIQDocument } from '../../../../services/hooks/PPPIQ/useDeletePPPIQDocument';
import { useRequestMyRegistration } from '../../../../services/hooks/CandidateArea/useRequestMyRegistration';

const MAX_FILES = 5;
const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const ACCEPTED_EXTENSIONS = '.pdf,.png,.jpg,.jpeg';

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PPPIQUploadPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('registration') ?? '';
  const navigate = useNavigate();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteName, setPendingDeleteName] = useState<string>('');
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [isVeracityChecked, setIsVeracityChecked] = useState(false);
  const [isVeracityOpen, setIsVeracityOpen] = useState(true);

  const { data: registrations } = useRequestMyRegistration();
  const { data: documents, isLoading, error, refetch } = useGetPPPIQDocuments(registrationId);
  const uploadMutation = useUploadPPPIQDocument(registrationId);
  const deleteMutation = useDeletePPPIQDocument(registrationId);

  const matchedRegistration = registrations
    ?.flatMap((r) => [r.registration])
    .find((r) => r.registration_id === registrationId);

  const actions = matchedRegistration?.actions;

  const pppiqStage = matchedRegistration?.announcement_stages_convocations?.find(
    (s) => s.receive_self_declaration_documents,
  );

  const periodStart =
    pppiqStage?.receive_self_declaration_documents_start ?? actions?.receive_self_declaration_documents_start ?? null;
  const periodEnd =
    pppiqStage?.receive_self_declaration_documents_end ?? actions?.receive_self_declaration_documents_end ?? null;

  const now = new Date();
  const start = periodStart ? new Date(periodStart) : null;
  const end = periodEnd ? new Date(periodEnd) : null;
  const isPeriodOpen = (!start || now >= start) && (!end || now <= end);

  const currentCount = documents?.length ?? 0;
  const canUpload = isPeriodOpen && currentCount < MAX_FILES;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({
        title: 'Formato inválido',
        description: 'Apenas arquivos PDF, PNG e JPG são aceitos.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (currentCount >= MAX_FILES) {
      toast({
        title: 'Limite atingido',
        description: `Você já enviou o máximo de ${MAX_FILES} declarações.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await uploadMutation.mutateAsync(file);
      await refetch();
      toast({
        title: 'Arquivo enviado com sucesso!',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch {
      toast({
        title: 'Erro ao enviar arquivo',
        description: 'Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (docId: string) => {
    onDeleteClose();
    setDeletingId(docId);
    console.log(
      `[PPPIQ] Removendo documento: id=${docId}, registration=${registrationId}, timestamp=${new Date().toISOString()}`,
    );
    try {
      await deleteMutation.mutateAsync(docId);
      await refetch();
      console.log(`[PPPIQ] Documento removido com sucesso: id=${docId}`);
      toast({
        title: 'Arquivo removido.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch {
      console.error(`[PPPIQ] Falha ao remover documento: id=${docId}`);
      toast({
        title: 'Erro ao remover arquivo',
        description: 'Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
      setPendingDeleteId(null);
      setPendingDeleteName('');
    }
  };

  const openDeleteConfirm = (docId: string, docName: string) => {
    setPendingDeleteId(docId);
    setPendingDeleteName(docName);
    onDeleteOpen();
  };

  return (
    <MainCard title="ÁREA DO CANDIDATO" subtitle="- PPPIQ - AUTODECLARAÇÃO">
      <Stack px={['20px', '20px', '30px', '50px']} pb={8} gap={4}>
        {/* Page title */}
        <Box>
          <Text fontSize="lg" fontWeight="bold">
            Envio de Documentos — Verificação Documental Complementar à Autodeclaração (PPPIQ)
          </Text>
          <Text fontSize="sm" color="gray.600">
            Envie sua documentação comprobatória de pertencimento étnico (PPPIQ). Você pode anexar até {MAX_FILES}{' '}
            arquivos nos formatos PDF, PNG ou JPG.
          </Text>
        </Box>

        {/* Guidance */}
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon mt={1} />
          <Stack gap={1}>
            <Text fontSize="sm" fontWeight="bold">
              Atenção — leia antes de enviar:
            </Text>
            <Text fontSize="sm">
              • <strong>Período de envio:</strong> {formatDate(periodStart)} até {formatDate(periodEnd)}
              {!isPeriodOpen && (
                <Text as="span" fontWeight="bold" color="red.600" ml={2}>
                  — Período encerrado. Não é possível enviar ou alterar documentos.
                </Text>
              )}
            </Text>
            <Text fontSize="sm">
              • A documentação deve estar <strong>legível, completa e em conformidade</strong> com as exigências
              editalícias.
            </Text>
            <Text fontSize="sm">
              • É de <strong>inteira responsabilidade do candidato</strong> a veracidade das informações prestadas e a
              adequada instrução do procedimento.
            </Text>
          </Stack>
        </Alert>

        {/* Period info */}
        {!isPeriodOpen && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm" fontWeight="bold">
              O período de envio está encerrado. Não é possível enviar ou alterar documentos.
            </Text>
          </Alert>
        )}

        {/* File counter */}
        <Flex
          align="center"
          gap={3}
          px={4}
          py={3}
          borderRadius="md"
          border="1px solid"
          borderColor={currentCount >= MAX_FILES ? 'red.200' : 'green.200'}
          bg={currentCount >= MAX_FILES ? 'red.50' : 'green.50'}
          w="fit-content"
        >
          <Text fontSize="md" fontWeight="bold" color={currentCount >= MAX_FILES ? 'red.700' : 'green.700'}>
            Declarações enviadas:
          </Text>
          <Badge
            fontSize="md"
            px={3}
            py={1}
            borderRadius="full"
            colorScheme={currentCount >= MAX_FILES ? 'red' : 'green'}
          >
            {currentCount} / {MAX_FILES}
          </Badge>
        </Flex>

        {/* Upload input hidden */}
        {isPeriodOpen && (
          <Input ref={inputRef} type="file" accept={ACCEPTED_EXTENSIONS} display="none" onChange={handleFileChange} />
        )}

        {/* Document list */}
        {isLoading && (
          <Flex justify="center" py={6}>
            <Spinner size="lg" color="blue.500" />
          </Flex>
        )}

        {!isLoading && error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            Erro ao carregar os documentos enviados. Tente recarregar a página.
          </Alert>
        )}

        {!isLoading && !error && documents && documents.length === 0 && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            Nenhuma declaração enviada ainda.
          </Alert>
        )}

        {!isLoading && documents && documents.length > 0 && (
          <Stack gap={2}>
            {documents.map((doc, idx) => (
              <Flex
                key={doc.id}
                align="center"
                justify="space-between"
                p={3}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                bg="gray.50"
              >
                <Flex align="center" gap={3} flex={1} minW={0}>
                  <Icon as={FiFile} color="blue.500" boxSize={5} flexShrink={0} />
                  <Stack gap={0} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                      {doc.name || `Declaração ${idx + 1}`}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      Enviado em: {formatDate(doc.created_at)}
                    </Text>
                  </Stack>
                </Flex>
                <Flex align="center" gap={2} flexShrink={0}>
                  <Button
                    as="a"
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                  >
                    Visualizar
                  </Button>
                  {isPeriodOpen && (
                    <IconButton
                      aria-label="Remover declaração"
                      icon={<FiTrash2 />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      isLoading={deletingId === doc.id}
                      isDisabled={deleteMutation.isPending}
                      onClick={() => openDeleteConfirm(doc.id, doc.name)}
                    />
                  )}
                </Flex>
              </Flex>
            ))}
          </Stack>
        )}
      </Stack>

      <Flex justify="flex-end" px={['20px', '20px', '30px', '50px']} pb={8} gap={3}>
        {isPeriodOpen && (
          <Button
            leftIcon={<Icon as={FiUpload} />}
            colorScheme="green"
            isDisabled={!canUpload || uploadMutation.isPending}
            isLoading={uploadMutation.isPending}
            loadingText="Enviando..."
            onClick={() => inputRef.current?.click()}
          >
            {currentCount >= MAX_FILES ? 'Limite de arquivos atingido' : 'Adicionar declaração'}
          </Button>
        )}
        <Button leftIcon={<IoMdReturnLeft />} colorScheme="blue" onClick={() => navigate('/painel/inscricoes')}>
          Voltar
        </Button>
      </Flex>

      {/* Veracity declaration modal */}
      <Modal isOpen={isVeracityOpen} onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Declaração de Veracidade dos Documentos</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" mb={4}>
              Declaro, para os devidos fins, que os documentos e as informações por mim apresentados no procedimento
              de verificação documental complementar à autodeclaração são autênticos, íntegros e correspondem à
              verdade, responsabilizando-me por seu conteúdo. Declaro, ainda, estar ciente de que a constatação de
              falsidade, fraude ou má-fé poderá acarretar as consequências previstas no Edital nº 49/2026/CCV/UFC e na
              legislação aplicável, assegurados o contraditório e a ampla defesa.
            </Text>
            <Checkbox
              isChecked={isVeracityChecked}
              onChange={(e) => setIsVeracityChecked(e.target.checked)}
              fontWeight="semibold"
            >
              Li e declaro serem verdadeiras as informações e autênticos os documentos apresentados.
            </Checkbox>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              isDisabled={!isVeracityChecked}
              onClick={() => setIsVeracityOpen(false)}
            >
              Continuar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmar exclusão</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Tem certeza que deseja remover o arquivo{' '}
              <Text as="span" fontWeight="bold">
                {pendingDeleteName}
              </Text>
              ? Esta ação não pode ser desfeita.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onDeleteClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="red"
              isLoading={!!deletingId}
              onClick={() => pendingDeleteId && handleDelete(pendingDeleteId)}
            >
              Remover
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MainCard>
  );
};

export default PPPIQUploadPage;
